import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { FulfillmentSection } from './FulfillmentSection'
import { BehaviorSection } from './BehaviorSection'
import { OrderHelpSection } from './OrderHelpSection'
import { OrderStatusSection } from './OrderStatusSection'
import { OrderSummarySection } from './OrderSummarySection'
import { useCompletedOrder } from 'src/public/components/online_ordering/OrderContext'
import {
  OrderIdentifier,
  DiningOptionBehavior,
  useGetOrderStatusesQuery,
  ThirdPartyDeliveryStatus,
  TrackingDetails,
  useGenerateReceiptPdfMutation
} from 'src/apollo/onlineOrdering'
import {
  getUnifiedOrderStatusWithCompletedOrder,
  UnifiedOrderStatus
} from '../order_tracker/order_status_bar/statusUtil'
import { DeliveryPhotoSection } from './DeliveryPhotoSection'
import { OrderNotFoundSection } from './OrderNotFoundSection'
import { useTrackOrderConfirmationPage } from './useTrackOrderConfirmationPage'
import { getOrderStatusSectionTexts } from './getOrderStatusSectionTexts'
import { OrderConfirmationLoading } from './OrderConfirmationLoading'
import { AppCTASection } from './AppCTASection'
import {
  ToastLocalAppCTASection,
  useAppCTAVariant
} from './ToastLocalAppCTASection'
import { t } from 'src/translations/src'
import { OrderTrackerMap } from '../order_tracker/order_tracker_map/OrderTrackerMap'
import { CartContextProvider } from 'src/public/components/online_ordering/CartContext'
import { LoyaltyContextProvider } from 'src/public/components/online_ordering/LoyaltyContext'
import { LoyaltyCard, LoyaltyCardType } from '../../offers/LoyaltyCard'
import { ScreenWidth, useIsMobile } from 'src/shared/js/utils/WindowContext'
import PostOrderScreen from './PostOrderScreen'
import { useFlags } from 'src/shared/components/common/feature_flags/useFlags'
import useTracker from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'
import { TailSpin } from 'react-loader-spinner'

const SIXTY_SECONDS_MS = 60000
const MAX_RETRY = 5

type LocationState = {
  completedOrder?: any
}

const ToastBrandedLoyaltyCardColors = {
  illustrationColor: '#FF4C00',
  backgroundColor: '#FFFFFF',
  textColor: '#000000',
  borderColor: '#0000000f'
}

const OrderConfirmation = () => {
  const location = useLocation<LocationState>()
  const history = useHistory()
  const tracker = useTracker()
  const { completedOrder, loadingCompletedOrder, locationGuidFromUrl } =
    useCompletedOrder()
  const isMobile = useIsMobile(ScreenWidth.SMALL)
  const appCTAVariant = useAppCTAVariant()
  const [readyToQuery, setReadyToQuery] = useState(false)
  const isTdsOrder = !!completedOrder?.deliveryServiceInfo

  // TDS needs a few seconds to actually dispatch the order to get tracking details.
  // If we query for it immediately, it may error and we won't have tracking information
  // until the next polling interval, so we build in a short delay to give them time to
  // get the order dispatched before querying for it.
  useEffect(() => {
    if (!isTdsOrder) {
      setReadyToQuery(true)
      return
    }
    setReadyToQuery(false)
    const timeoutId = setTimeout(() => {
      setReadyToQuery(true)
    }, 3000)
    return () => clearTimeout(timeoutId)
  }, [isTdsOrder])

  const skipQuery = useMemo(
    () => !completedOrder?.guid || !readyToQuery,
    [completedOrder?.guid, readyToQuery]
  )

  const retryCountRef = useRef<number>(0)
  const { ooOrderSuccessPage } = useFlags()

  // PostOrderScreen state - show only on fresh order from checkout and if feature flag is enabled
  const [showPostOrderScreen, setShowPostOrderScreen] = useState<boolean>(
    () => !!location.state?.completedOrder && ooOrderSuccessPage
  )

  const handleDismissPostOrderScreen = useCallback(() => {
    setShowPostOrderScreen(false)
    // Clear location state to prevent PostOrderScreen from showing again on refresh
    history.replace(location.pathname, {})
  }, [history, location.pathname])

  const orderIdentifier: OrderIdentifier = {
    orderGuid: completedOrder?.guid ?? '',
    restaurantGuid: completedOrder?.restaurant.guid ?? '',
    diningOptionBehavior:
      completedOrder?.diningOptionBehavior ?? DiningOptionBehavior.Delivery,
    isThirdPartyDeliveryServiceOrder: !!completedOrder?.deliveryServiceInfo
  }
  const {
    data,
    stopPolling,
    loading: orderStatusInfoLoading
  } = useGetOrderStatusesQuery({
    variables: { input: { orderIdentifiers: [orderIdentifier] } },
    pollInterval: SIXTY_SECONDS_MS,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    skip: skipQuery,
    ssr: false,
    onError: () => {
      const nextRetryCount = retryCountRef.current + 1
      if (nextRetryCount >= MAX_RETRY) {
        stopPolling()
      } else {
        retryCountRef.current = nextRetryCount
      }
    },
    onCompleted: (newData) => {
      retryCountRef.current = 0
      if (
        newData?.orderStatuses.orderStatusesResponse?.at(0)?.deliveryStatus ===
        ThirdPartyDeliveryStatus.OrderDelivered
      ) {
        stopPolling()
      }
    }
  })

  const orderStatusInfo = data?.orderStatuses.orderStatusesResponse?.at(0)
  const trackingDetails = orderStatusInfo?.trackingDetails

  const unifiedOrderStatus = useMemo(() => {
    return getUnifiedOrderStatusWithCompletedOrder(
      orderStatusInfo,
      orderStatusInfoLoading,
      completedOrder
    )
  }, [completedOrder, orderStatusInfo, orderStatusInfoLoading])

  const isDeliveryOrder =
    completedOrder?.diningOptionBehavior === DiningOptionBehavior.Delivery
  const showMap =
    isTdsOrder &&
    isDeliveryOrder &&
    unifiedOrderStatus !== UnifiedOrderStatus.COMPLETE &&
    unifiedOrderStatus !== UnifiedOrderStatus.CANCELLED &&
    unifiedOrderStatus !== UnifiedOrderStatus.DELIVERY_FAILED &&
    unifiedOrderStatus !== UnifiedOrderStatus.UNDELIVERABLE &&
    unifiedOrderStatus !== UnifiedOrderStatus.FAILURE_AT_RESTAURANT

  const confirmationHeaderText = useMemo(
    () =>
      getOrderStatusSectionTexts({
        unifiedOrderStatus,
        isDeliveryOrder,
        restaurantName: completedOrder?.restaurant?.name,
        customerEmail: completedOrder?.customerV3?.email,
        lastUpdatedIso: orderStatusInfo?.lastUpdated,
        approvalStatus: completedOrder?.approvalStatus
      }).headerText,
    [
      unifiedOrderStatus,
      isDeliveryOrder,
      completedOrder?.restaurant?.name,
      completedOrder?.customerV3?.email,
      orderStatusInfo?.lastUpdated,
      completedOrder?.approvalStatus
    ]
  )

  useTrackOrderConfirmationPage({
    unifiedOrderStatus,
    orderStatusInfoLoading,
    orderStatusInfo: orderStatusInfo,
    headerText: confirmationHeaderText,
    skip: showPostOrderScreen
  })

  const loyaltySection = completedOrder?.restaurant.loyaltyConfig
    ?.loyaltySignupEnabled ? (
    <CartContextProvider>
      <LoyaltyContextProvider>
        <LoyaltyCard
          type={LoyaltyCardType.OrderConfirmationPage}
          completedOrderBehavior={completedOrder.diningOptionBehavior}
          overrideColors={ToastBrandedLoyaltyCardColors}
        />
      </LoyaltyContextProvider>
    </CartContextProvider>
  ) : null

  // Calculate savings breakdown for tooltip
  const savingsBreakdown = useMemo(() => {
    if (!completedOrder) return undefined

    const loyaltyRewardAmount =
      completedOrder.discounts?.loyaltyDiscounts?.reduce(
        (sum, discount) => sum + (discount?.amount || 0),
        0
      ) || 0

    const promoCodeAmount = Math.max(
      completedOrder.discountsTotal - loyaltyRewardAmount,
      0
    )

    // TODO: [Backend Integration] Calculate delivery savings
    // Need backend to provide:
    // - Would have been delivery fee alongside the actual delivery fee
    const deliverySavingsAmount = 0

    return {
      promoCodeAmount,
      loyaltyRewardAmount,
      deliverySavingsAmount
    }
  }, [completedOrder])

  const [pdfError, setPdfError] = useState<string | null>(null)
  const [generateReceiptPdf, { loading: pdfLoading }] =
    useGenerateReceiptPdfMutation()

  const handlePrintClick = useCallback(async () => {
    if (!completedOrder) return

    tracker.track(TrackingEvents.DOWNLOAD_RECEIPT_PDF_BUTTON_CLICK, {
      restaurantGuid: completedOrder.restaurant.guid,
      ...(locationGuidFromUrl && {
        orderGuidInUrl: locationGuidFromUrl
      })
    })

    setPdfError(null)

    try {
      const result = await generateReceiptPdf({
        variables: {
          orderGuid: completedOrder.guid,
          restaurantGuid: completedOrder.restaurant.guid,
          checkGuid: completedOrder.checkGuid
        }
      })

      const pdfResult = result.data?.digitalReceipts?.generateReceiptPdf

      if (pdfResult?.__typename === 'GenerateReceiptPdfResponse') {
        const bytes = Uint8Array.from(atob(pdfResult.pdfBase64), (c) =>
          c.charCodeAt(0)
        )
        const blob = new Blob([bytes], { type: 'application/pdf' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'receipt.pdf'
        document.body.appendChild(a)
        a.click()
        a.remove()
        setTimeout(() => URL.revokeObjectURL(url), 100)
      } else {
        setPdfError(t('order-confirmation.download-error'))
      }
    } catch {
      setPdfError(t('order-confirmation.download-error'))
    }
  }, [completedOrder, generateReceiptPdf, locationGuidFromUrl, tracker])

  if ((loadingCompletedOrder && !completedOrder) || (isTdsOrder && skipQuery)) {
    return <OrderConfirmationLoading />
  }

  return (
    <>
      {showPostOrderScreen && completedOrder ? (
        <PostOrderScreen
          discountTotal={completedOrder.discountsTotal}
          savingsBreakdown={savingsBreakdown}
          pointsEarned={completedOrder.estimatedLoyaltyPointAccrual}
          pointsEarnedMessage={completedOrder.estimatedLoyaltyAccrualLabel}
          customerEmail={completedOrder.customerV3?.email ?? ''}
          restaurantGuid={completedOrder.restaurant?.guid}
          onDismiss={handleDismissPostOrderScreen}
        />
      ) : (
        <div className='orderConfirmation pdfPrint'>
          {completedOrder ? (
            <>
              <div className='leftColumnWrapper'>
                <div className='contentWrapper'>
                  <OrderStatusSection
                    orderStatusInfo={orderStatusInfo}
                    orderStatusInfoLoading={orderStatusInfoLoading}
                  />
                  <DeliveryPhotoSection
                    trackingDetails={trackingDetails as TrackingDetails}
                  />
                  {showMap && (
                    <div className='orderConfirmationSection smallPadding'>
                      <OrderTrackerMap
                        order={completedOrder}
                        orderStatusData={orderStatusInfo}
                      />
                    </div>
                  )}
                  {unifiedOrderStatus !== UnifiedOrderStatus.COMPLETE &&
                    unifiedOrderStatus !==
                      UnifiedOrderStatus.READY_FOR_PICKUP && (
                      <FulfillmentSection
                        trackingDetails={trackingDetails ?? undefined}
                      />
                    )}
                  {appCTAVariant === 'toastLocal' && (
                    <ToastLocalAppCTASection />
                  )}
                  {unifiedOrderStatus !== UnifiedOrderStatus.COMPLETE && (
                    <BehaviorSection
                      deliveryDriver={trackingDetails?.deliveryDriver}
                      unifiedOrderStatus={unifiedOrderStatus}
                    />
                  )}
                  {!isMobile && (
                    <>
                      {loyaltySection}
                      {appCTAVariant === 'default' && <AppCTASection />}
                    </>
                  )}
                </div>
              </div>
              <div className='rightColumnWrapper'>
                <div className='contentWrapper'>
                  <OrderSummarySection />
                  <div className='downloadWrapper pdfHide'>
                    <button
                      data-testid='pdf-download-link'
                      onClick={handlePrintClick}
                      disabled={pdfLoading}
                      aria-busy={pdfLoading}
                      aria-label={t('order-confirmation.download')}
                    >
                      {pdfLoading ? (
                        <TailSpin
                          color={'#646464'}
                          width={'16px'}
                          height={'16px'}
                          strokeWidth={'6px'}
                        />
                      ) : (
                        t('order-confirmation.download')
                      )}
                    </button>
                    {pdfError && (
                      <p data-testid='pdf-download-error' role='alert'>
                        {pdfError}
                      </p>
                    )}
                  </div>
                  {/* "Need help?" card sits below the Download PDF action. This is the
                      completed-order mount, so it is the one that may offer recovery;
                      OrderHelpSection still self-gates (recovery vs legacy phone card vs
                      nothing). The wrapper carries the group divider that sets the card off
                      from the payment group above — this placement is the only one that sits
                      below that group, and `.orderHelpGroup:not(:empty)` drops the divider
                      when the section gates itself out. In-progress orders get the phone
                      card from BehaviorSection instead. */}
                  {unifiedOrderStatus === UnifiedOrderStatus.COMPLETE && (
                    <div className='orderHelpGroup'>
                      <OrderHelpSection allowRecoveryCard />
                    </div>
                  )}
                  {isMobile && (
                    <>
                      {loyaltySection}
                      {appCTAVariant === 'default' && <AppCTASection />}
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (
            <OrderNotFoundSection />
          )}
        </div>
      )}
    </>
  )
}

export default OrderConfirmation
